import React, { type ReactNode } from 'react'
import { useTranslation } from 'next-i18next'
import { twMerge } from 'tailwind-merge'
import CloseIcon from 'shared/icons/close-icon'
import SearchIcon from 'shared/icons/search-icon'

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label?: string
  error?: string
  inputClassName?: string
  onClear: () => void
  placeholder?: string
  description?: string
  testAttributePostfix?: string
  isPreFetching?: boolean
  labelClassName?: string
}

function FormInputSearch({
  label,
  value,
  inputClassName,
  onChange,
  onFocus,
  maxLength,
  error,
  placeholder,
  onClear,
  description,
  isPreFetching,
  labelClassName,
  className,
  ...rest
}: InputProps): ReactNode {
  const { t } = useTranslation()

  return (
    <label
      className={twMerge(
        'relative flex flex-col gap-1 text-sm font-medium',
        labelClassName,
        className,
      )}
    >
      {label && <span>{label}</span>}
      {value === '' ? (
        <SearchIcon
          className={twMerge(
            `absolute right-4 z-10 size-[21px] translate-y-1/2 scale-75 text-(--theme-primary,var(--text-primary))`,
            label && 'top-6',
          )}
        />
      ) : (
        <CloseIcon
          onClick={onClear}
          className={twMerge(
            `absolute right-2 z-10 size-[21px] translate-y-1/2 scale-75 cursor-pointer text-(--theme-primary,var(--text-primary))`,
            label && 'top-6',
          )}
        />
      )}
      <input
        className={twMerge(
          `relative w-full rounded-lg border text-text-primary main-transition-colors`,
          `py-2.5 pr-8 pl-4 placeholder:text-text-tertiary/70 focus:border-brand-primary focus:outline-hidden`,
          error ? 'border-danger' : 'border-text-secondary/30',
          isPreFetching
            ? `animate-pulse bg-skeleton-primary text-transparent disabled:bg-skeleton-primary/40`
            : 'disabled:bg-background-tertiary/40',
          inputClassName,
        )}
        onKeyDown={evt =>
          rest.type === 'number' && ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()
        }
        type={rest.type || 'text'}
        disabled={rest.disabled || isPreFetching}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        maxLength={maxLength}
        placeholder={placeholder || t('global.search')}
        spellCheck={false}
        {...rest}
      />
      {description && <span className="text-sm opacity-70">{description}</span>}
      {error && <p className="text-sm text-danger">{error}</p>}
    </label>
  )
}

export default FormInputSearch
